import {css, view} from '@sail/ui/deprecated';
import {defineMessages, FormattedMessage} from 'react-intl';
import {Icon, Tooltip} from '@sail/ui';
import {info} from '@sail/icons/react/Icon';
import {useEffect, useRef} from 'react';
import {Conversation} from '@stripe-internal/conversations-client';
import {MetaRow} from './MetaRow';
import {useUniversalHelpDotComDesign} from '../../FeatureFlagsProvider';
import {useTheme} from '../../ThemeProvider';

const messages = defineMessages({
  assistantGreeting: {
    id: 'conversations_ui.support_conversations_client.universal_help_client.assistantGreetingV2',
    description: 'Greeting message and disclaimer about the Assistant and AI.',
    defaultMessage: "Hi, I'm Stripe Assistant.",
  },
  linkAssistantGreeting: {
    id: 'conversations_ui.support_conversations_client.universal_help_client.linkAssistantGreeting',
    description:
      'Welcome message for Link and disclaimer about the Assistant and AI.',
    defaultMessage: 'Welcome to Link Support.',
  },
  assistantGreetingTooltip: {
    id: 'conversations_ui.support_conversations_client.universal_help_client.assistantGreetingTooltip',
    description: 'Tooltip message for the assistant greeting',
    defaultMessage:
      'Some of my answers are generated using AI and may occasionally be incorrect.',
  },
});

type Props = {
  conversation: Conversation;
};

export const ChatStartedRow = ({conversation}: Props) => {
  const useDotComDesign = useUniversalHelpDotComDesign();

  const {client} = conversation;
  const theme = useTheme();
  const hasInitialQuery = Boolean(
    conversation.context?.triggerParams?.initialQuery,
  );

  const hasShownGreeting = client.hasShownInitialGreeting?.() || false;
  const initialShowGreeting = !hasShownGreeting && !hasInitialQuery;

  const showGreeting = useRef(initialShowGreeting);

  useEffect(() => {
    if (showGreeting.current) {
      client.markInitialGreetingAsShown?.();
    }
  }, [client]);

  if (useDotComDesign) {
    return null;
  }

  const disclaimerText = (
    <FormattedMessage {...messages.assistantGreetingTooltip} />
  );

  const greetingText = (
    <FormattedMessage
      {...(theme.audience === 'link'
        ? messages.linkAssistantGreeting
        : messages.assistantGreeting)}
    />
  );

  return (
    <view.div
      data-testid="chat-started-row"
      css={{
        // This is a little hacky, but there is always a message from
        // the backend below this. However, because they are two
        // separate messages they use the gap from the log items which
        // is too large for text.
        marginBottom: css.value('-8px'),
      }}
    >
      <view.div
        css={{
          stack: 'x',
          alignX: 'start',
          alignY: 'center',
          // This prevents jank when the greeting message switches to the disclaimer
          // because the disclaimer is slightly taller the greeting message (32px vs 28px)
          minHeight: css.value('32px'),
        }}
      >
        <view.span>
          {showGreeting.current ? (
            greetingText
          ) : (
            <MetaRow>{disclaimerText}</MetaRow>
          )}
        </view.span>
        {showGreeting.current ? (
          <Tooltip
            type="description"
            placement="top"
            trigger={
              <Icon
                data-testid="chat-started-info-icon"
                icon={info}
                size="xsmall"
                css={{fill: 'subdued', marginLeft: 'space.75'}}
              />
            }
          >
            {disclaimerText}
          </Tooltip>
        ) : null}
      </view.div>
    </view.div>
  );
};
